// src/components/MeisaiPanel.tsx (2026-06-16 新設)
// 「明細分析」タブ = 診療行為/加算の増減 movers。明細(医事コード粒度)を 点数マスタで名寄せした
//   meisai_act.duckdb を直読み (別ファイル方式)。直近12ヶ月 vs 前年同期 の増減で 増加/減少TOP。
//   入院/外来・診療No分類・点数/金額 で絞り込み。加算・手術の増えてる/減ってるを発見する用途。
import { useQuery } from "@tanstack/react-query";
import { useFilters } from "../store";
import { api } from "../lib/api";
import type { MeisaiMoversResponse, MeisaiMoverRow } from "@shared/api";
import { useSort, SortTh } from "../lib/useSort";

const k = (v: number | null): string => v == null ? "—" : Math.round(v / 1000).toLocaleString();

export function MeisaiPanel() {
  const f = useFilters();
  // フィルタはサイドバー (区分/基準/診療No分類/診療科 + 表示形式) で制御
  const io = f.meisaiIo, basis = f.meisaiBasis, sinryoNo = f.meisaiSinryoNo, dept = f.meisaiDept;
  const win = f.periodPreset === "直近12ヶ月平均" ? 12 : 1;

  const { data, isLoading, error } = useQuery<MeisaiMoversResponse>({
    queryKey: ["meisai_movers", f.asOfMonth, io, basis, sinryoNo, dept, win],
    queryFn: () => api.meisaiMovers({ asOfMonth: f.asOfMonth, io, basis, sinryoNo: sinryoNo || undefined, dept: dept || undefined, window: win, topN: 30 }),
    placeholderData: (p) => p,
  });

  const unit = basis === "ten" ? "千点" : "千円";
  const curTo = f.asOfMonth.slice(0, 7);
  const winLabel = win === 12 ? `直近12ヶ月（〜${curTo}）÷ 前年同期` : `${curTo} ÷ 前年同月`;
  const deptLabel = dept ? dept.replace(/^HP\)/, "") : "全科";

  if (data && !data.available) {
    return (
      <div className="card p-6 text-sm text-slate-600">
        明細分析データ (meisai_act.duckdb) が未生成です。明細×点数マスタの集計ファイルを配置すると有効になります。
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <section className="card p-3 no-print">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-base font-semibold">🔎 明細分析（診療行為・加算の増減）</h2>
          <span className="text-xs text-slate-500">
            {winLabel} の {unit} 増減で 増加TOP / 減少TOP（{io}・{deptLabel}{sinryoNo ? `・${sinryoNo}` : ""}）。フィルタはサイドバーで。
          </span>
          {isLoading && <span className="text-xs text-slate-400 ml-auto">loading…</span>}
        </div>
        <div className="text-[11px] text-slate-500 mt-1 leading-tight">
          ▸ 集計 = 期間内の{unit}<b>合計</b>（月平均でなく累計）／ 件数 = レセプト<b>算定回数</b>（患者数ではない）／ 増減の色 = 方向（増減であって良否ではない）
        </div>
      </section>

      {error && <div className="card p-4 text-sm text-red-700 bg-red-50">データ取得エラー: {(error as Error).message}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <MoverTable title="📈 増加 TOP30" rows={data?.increasers ?? []} unit={unit} up />
        <MoverTable title="📉 減少 TOP30" rows={data?.decreasers ?? []} unit={unit} up={false} />
      </div>
    </div>
  );
}

function MoverTable({ title, rows, unit, up }: { title: string; rows: MeisaiMoverRow[]; unit: string; up: boolean }) {
  // 既定は server 由来の増減順(rows 原順)を維持。ヘッダクリックで並べ替え可能に。
  const s = useSort(rows);
  return (
    <section className="card p-3">
      <h3 className={`text-sm font-semibold mb-2 ${up ? "text-emerald-700" : "text-red-700"}`}>{title}</h3>
      <div className="overflow-x-auto text-xs">
        <table className="w-full border-collapse">
          <thead className="bg-slate-100">
            <tr>
              <SortTh s={s} sortKey="name" get={(r) => (r as MeisaiMoverRow).name || (r as MeisaiMoverRow).code} className="text-left px-2 py-1 border-b border-slate-200">診療行為 / 加算</SortTh>
              <SortTh s={s} sortKey="sinryoNo" get={(r) => (r as MeisaiMoverRow).sinryoNo} className="text-left px-1.5 py-1 border-b border-slate-200 w-16">診療No分類</SortTh>
              <SortTh s={s} sortKey="curCnt" get={(r) => (r as MeisaiMoverRow).curCnt} className="text-right px-1.5 py-1 border-b border-slate-200">算定回数</SortTh>
              <SortTh s={s} sortKey="prevCnt" get={(r) => (r as MeisaiMoverRow).prevCnt} className="text-right px-1.5 py-1 border-b border-slate-200">前年回数</SortTh>
              <SortTh s={s} sortKey="cur" get={(r) => (r as MeisaiMoverRow).cur} className="text-right px-1.5 py-1 border-b border-slate-200">当期({unit})</SortTh>
              <SortTh s={s} sortKey="prev" get={(r) => (r as MeisaiMoverRow).prev} className="text-right px-1.5 py-1 border-b border-slate-200">前年</SortTh>
              <SortTh s={s} sortKey="diff" get={(r) => (r as MeisaiMoverRow).diff} className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">増減</SortTh>
              <SortTh s={s} sortKey="ratio" get={(r) => (r as MeisaiMoverRow).ratio} className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">増減率</SortTh>
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r) => {
              const cntDiff = (r.curCnt != null && r.prevCnt != null) ? r.curCnt - r.prevCnt : null;
              return (
              <tr key={r.code} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="text-left px-2 py-0.5" title={`${r.code}`}>{r.name || r.code}</td>
                <td className="text-left px-1.5 py-0.5 text-slate-500">{r.sinryoNo}</td>
                <td className="text-right px-1.5 py-0.5 tabular-nums" title={cntDiff != null ? `前年差 ${cntDiff > 0 ? "+" : ""}${cntDiff.toLocaleString()}` : ""}>
                  {r.curCnt != null ? Math.round(r.curCnt).toLocaleString() : "—"}
                </td>
                <td className="text-right px-1.5 py-0.5 tabular-nums text-slate-500">
                  {r.prevCnt != null ? Math.round(r.prevCnt).toLocaleString() : "—"}
                </td>
                <td className="text-right px-1.5 py-0.5 tabular-nums">{k(r.cur)}</td>
                <td className="text-right px-1.5 py-0.5 tabular-nums text-slate-500">{k(r.prev)}</td>
                <td className={`text-right px-1.5 py-0.5 tabular-nums font-semibold ${up ? "text-emerald-700" : "text-red-600"}`}>
                  {r.diff != null && r.diff > 0 ? "+" : ""}{k(r.diff)}
                </td>
                <td className="text-right px-1.5 py-0.5 tabular-nums text-slate-600">
                  {r.ratio != null ? `${(r.ratio * 100).toFixed(0)}%` : (r.prev == null || r.prev === 0) ? "新規" : "—"}
                </td>
              </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-6">データなし</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

